import jsPDF from 'jspdf'
import dayjs from 'dayjs'
import aaremoFallbackLogo from '@/assets/images/logo/aaremoTextBottom.png'
import { useThemeStore } from '@/stores/system/theme'
import { useUserStore } from '@/stores/system/user'
import { useBackendStore } from '@/stores/system/backend'

export type Rgb = [number, number, number]

export const FALLBACK_PRIMARY_RGB: Rgb = [0, 164, 165]

export function hexToRgb(hex: string | null | undefined): Rgb | null {
	if (!hex || typeof hex !== 'string') return null
	const m = hex.replace('#', '').trim()
	if (m.length !== 3 && m.length !== 6) return null
	const expand =
		m.length === 3
			? m
					.split('')
					.map((c) => c + c)
					.join('')
			: m
	const num = parseInt(expand, 16)
	if (Number.isNaN(num)) return null
	return [(num >> 16) & 0xff, (num >> 8) & 0xff, num & 0xff]
}

export function tintRgb(rgb: Rgb, factor = 0.92): Rgb {
	const blend = (c: number) => Math.round(255 - (255 - c) * (1 - factor))
	return [blend(rgb[0]), blend(rgb[1]), blend(rgb[2])]
}

export function stripHtml(input: string | null | undefined): string {
	if (typeof input !== 'string') return ''
	if (input.indexOf('<') === -1) return input
	const parser = new DOMParser()
	try {
		const doc = parser.parseFromString(`<div>${input}</div>`, 'text/html')
		return (doc.body.textContent || '').replace(/[ \t]+/g, ' ').trim()
	} catch {
		return input.replace(/<[^>]*>/g, '').replace(/[ \t]+/g, ' ').trim()
	}
}

export function sanitizeFilenamePart(value: string): string {
	return value
		.replace(/[\\/:*?"<>|]+/g, '')
		.replace(/\s+/g, '_')
		.slice(0, 80)
}

export function imageMetaFromDataUrl(dataUrl: string): { format: string } {
	const match = /^data:image\/(png|jpeg|jpg|webp|gif);/i.exec(dataUrl)
	const fmt = (match?.[1] || 'PNG').toUpperCase()
	return { format: fmt === 'JPG' ? 'JPEG' : fmt }
}

let cachedClientLogo: string | null = null
let cachedAaremoFallback: string | null = null

async function urlToDataUrl(url: string): Promise<string | null> {
	if (!url) return null
	if (url.startsWith('data:')) return url
	try {
		const response = await fetch(url, { credentials: 'omit' })
		if (!response.ok) return null
		const blob = await response.blob()
		return await new Promise<string>((resolve, reject) => {
			const reader = new FileReader()
			reader.onerror = () => reject(reader.error)
			reader.onload = () => resolve(String(reader.result))
			reader.readAsDataURL(blob)
		})
	} catch {
		return null
	}
}

async function loadAaremoFallbackDataUrl(): Promise<string | null> {
	if (cachedAaremoFallback) return cachedAaremoFallback
	cachedAaremoFallback = await urlToDataUrl(aaremoFallbackLogo)
	return cachedAaremoFallback
}

export async function loadAaremoLogoDataUrl(): Promise<string | null> {
	return await loadAaremoFallbackDataUrl()
}

export async function loadLogoDataUrl(): Promise<string | null> {
	if (cachedClientLogo) return cachedClientLogo

	const themeStore = useThemeStore()
	const candidates: string[] = []
	if (themeStore.logo) candidates.push(themeStore.logo)
	if (themeStore.defaultLogo && themeStore.defaultLogo !== themeStore.logo) {
		candidates.push(themeStore.defaultLogo)
	}

	for (const candidate of candidates) {
		const dataUrl = await urlToDataUrl(candidate)
		if (dataUrl) {
			cachedClientLogo = dataUrl
			return cachedClientLogo
		}
	}

	return await loadAaremoFallbackDataUrl()
}

export function resolvePrimaryRgb(): Rgb {
	const themeStore = useThemeStore()
	return hexToRgb(themeStore.theme?.color_l) || FALLBACK_PRIMARY_RGB
}

export function collectPdfMetadata(): {
	user: string
	tenant: string
	path: string
} {
	let user = '-'
	let tenant = '-'
	let path = '-'
	try {
		const userStore = useUserStore()
		const wa = userStore.whoami
		if (wa) {
			const teamName = wa.team?.full_name || ''
			user = teamName || wa.name || wa.email || '-'
		}
	} catch {
		// store not available
	}
	try {
		const backendStore = useBackendStore()
		const sub = backendStore.url?.subdomain || ''
		const dom = backendStore.url?.domain || ''
		if (sub && dom && dom.startsWith(`${sub}.`)) {
			tenant = dom
		} else if (sub && dom) {
			tenant = `${sub}.${dom}`
		} else {
			tenant = sub || dom || '-'
		}
	} catch {
		// store not available
	}
	if (typeof window !== 'undefined' && window.location) {
		path = (window.location.pathname || '') + (window.location.hash || '')
		if (!path) path = '-'
	}
	return { user, tenant, path }
}

export interface DrawHeaderOptions {
	title: string
	margin: number
	forceAaremoLogo?: boolean
}

export interface DrawHeaderResult {
	headerBottomY: number
}

export async function drawPdfHeader(
	doc: jsPDF,
	opts: DrawHeaderOptions
): Promise<DrawHeaderResult> {
	const { title, margin, forceAaremoLogo } = opts
	const pageWidth = doc.internal.pageSize.getWidth()
	const primaryRgb = resolvePrimaryRgb()

	const logoDataUrl = forceAaremoLogo
		? await loadAaremoLogoDataUrl()
		: await loadLogoDataUrl()
	const logoBoxW = 90
	const logoBoxH = 32

	if (logoDataUrl) {
		try {
			const imgMeta = imageMetaFromDataUrl(logoDataUrl)
			doc.addImage(
				logoDataUrl,
				imgMeta.format,
				margin,
				margin - 6,
				logoBoxW,
				logoBoxH,
				undefined,
				'FAST'
			)
		} catch {
			// silently skip logo on render failure
		}
	}

	doc.setFont('helvetica', 'bold')
	doc.setFontSize(16)
	doc.setTextColor(primaryRgb[0], primaryRgb[1], primaryRgb[2])
	doc.text(title.toUpperCase(), pageWidth - margin, margin + 8, {
		align: 'right',
	})

	doc.setDrawColor(primaryRgb[0], primaryRgb[1], primaryRgb[2])
	doc.setLineWidth(1.2)
	const dividerY = margin + logoBoxH + 4
	doc.line(margin, dividerY, pageWidth - margin, dividerY)

	return { headerBottomY: dividerY + 4 }
}

export interface DrawFooterOptions {
	margin: number
	tenantLabel: string
	userLabel: string
	pathLabel: string
	generatedAtLabel: string
	pageOfPagesText: (current: number, total: number) => string
}

export function drawPdfFooter(doc: jsPDF, opts: DrawFooterOptions): void {
	const pageWidth = doc.internal.pageSize.getWidth()
	const pageHeight = doc.internal.pageSize.getHeight()
	const primaryRgb = resolvePrimaryRgb()
	const meta = collectPdfMetadata()
	const generatedAt = dayjs().format('DD.MM.YYYY HH:mm')

	const footerLineGap = 10
	const footerBottomPad = opts.margin / 2
	const footerLine2Y = pageHeight - footerBottomPad
	const footerLine1Y = footerLine2Y - footerLineGap
	const footerSepY = footerLine1Y - 8

	const labelMutedColor: Rgb = [150, 150, 150]
	const valueColor: Rgb = [80, 80, 80]

	const writeFooterPair = (label: string, value: string, x: number, y: number) => {
		doc.setFont('helvetica', 'bold')
		doc.setTextColor(...labelMutedColor)
		doc.text(`${label}:`, x, y)
		const labelWidth = doc.getTextWidth(`${label}: `)
		doc.setFont('helvetica', 'normal')
		doc.setTextColor(...valueColor)
		doc.text(value, x + labelWidth, y)
	}

	const pageCount = doc.getNumberOfPages()
	for (let i = 1; i <= pageCount; i++) {
		doc.setPage(i)
		doc.setDrawColor(...primaryRgb)
		doc.setLineWidth(0.6)
		doc.line(opts.margin, footerSepY, pageWidth - opts.margin, footerSepY)

		doc.setFontSize(7)

		writeFooterPair(opts.tenantLabel, meta.tenant, opts.margin, footerLine1Y)
		const midX = pageWidth / 2 + 10
		writeFooterPair(opts.userLabel, meta.user, midX, footerLine1Y)

		writeFooterPair(opts.pathLabel, meta.path, opts.margin, footerLine2Y)
		writeFooterPair(opts.generatedAtLabel, generatedAt, midX, footerLine2Y)

		doc.setFont('helvetica', 'normal')
		doc.setTextColor(...valueColor)
		doc.text(
			opts.pageOfPagesText(i, pageCount),
			pageWidth - opts.margin,
			footerLine2Y,
			{ align: 'right' }
		)
	}
}

export function getFooterReservedHeight(margin: number): number {
	const footerLineGap = 10
	const footerBottomPad = margin / 2
	const sepOffset = 8
	const padding = 12
	return footerBottomPad + footerLineGap + sepOffset + padding
}
